import { useEffect, useRef } from 'react';
import type { MutableRefObject } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { io } from 'socket.io-client';
import { supportChatKeys } from '@/api/queries/support-chat/key';
import { parseAiSupportNewMessagePayload } from '@/lib/ai-support/parse-new-message-payload';
import { getOmnichannelSocketOrigin } from '@/lib/omnichannel/socket-url';
import { getSocketAuthToken } from '@/lib/auth/socket-auth-token';
import type { SupportChatMessage } from '@/types';

const AI_SUPPORT_MESSAGE_EVENT = 'ai_support_message';

export type AiSupportSocketHandlers = {
  conversationIdRef: MutableRefObject<string | number | null>;
  onAssistantReply: (
    message: SupportChatMessage,
    conversationId: string | number
  ) => void;
};

/**
 * Subscribes to AI support assistant replies on Socket.IO `ai_support_message`.
 */
export function useAiSupportSocket(
  enabled: boolean,
  handlers?: AiSupportSocketHandlers
) {
  const queryClient = useQueryClient();
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!enabled) return;

    const origin = getOmnichannelSocketOrigin();
    if (!origin) return;

    const token = getSocketAuthToken();

    const socket = io(origin, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      ...(token ? { auth: { token } } : {}),
    });

    const onAiSupportMessage = (raw: unknown) => {
      const parsed = parseAiSupportNewMessagePayload(raw);

      if (import.meta.env.DEV) {
        console.groupCollapsed(
          '[AI Support WebSocket] ai_support_message',
          new Date().toISOString()
        );
        console.log('raw payload:', raw);
        console.log('parsed:', parsed);
        console.groupEnd();
      }

      if (!parsed) return;

      void queryClient.invalidateQueries({
        queryKey: supportChatKeys.conversation(),
      });

      const h = handlersRef.current;
      if (!h) return;

      const activeConversationId = h.conversationIdRef.current;
      if (
        activeConversationId !== null &&
        String(parsed.conversationId) !== String(activeConversationId)
      ) {
        return;
      }

      h.onAssistantReply(parsed.message, parsed.conversationId);
    };

    socket.on(AI_SUPPORT_MESSAGE_EVENT, onAiSupportMessage);

    return () => {
      socket.off(AI_SUPPORT_MESSAGE_EVENT, onAiSupportMessage);
      socket.disconnect();
    };
  }, [enabled, queryClient]);
}
